import { useState, useEffect, useCallback } from "react";
import { trackOnce } from "./track.js";
import { validWorkspace } from "./validateWorkspace.js";
const KEY = "fundraising-workspace-v1";

/**
 * Ai đang sở hữu kho dữ liệu trên trình duyệt này.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * VÌ SAO PHẢI TÁCH THEO TÀI KHOẢN: trước đây mọi người dùng chung đúng một
 * khoá localStorage. Hai founder dùng chung một máy — A đăng xuất, B đăng
 * nhập — thì runway và cap table của A nằm sẵn đó và bị đẩy lên tài khoản B.
 * Đó là rò rỉ dữ liệu giữa hai người, không phải lỗi tiện ích.
 *
 * Nay mỗi tài khoản một kho riêng, khách chưa đăng nhập một kho riêng. Không
 * có kho nào tự chảy sang kho nào; muốn mang dữ liệu khách vào tài khoản thì
 * người dùng phải tự bấm.
 * ─────────────────────────────────────────────────────────────────────────
 */
let uidHienTai = null;

/** Bộ nhớ tạm của form, cũng phải xoá khi đổi người — nếu không B mở công cụ
 *  ra vẫn thấy số A vừa nhập. */
let memory = {};

function khoa(uid = uidHienTai) {
  return uid ? `${KEY}:${uid}` : KEY;
}

/** Kho khách — nơi dữ liệu của người chưa đăng nhập nằm. */
export function docKhoKhach() {
  return docKho(khoa(null));
}

export function xoaKhoKhach() {
  try { localStorage.removeItem(khoa(null)); } catch { /* bỏ qua */ }
}

/**
 * Đổi kho đang dùng. Gọi khi đăng nhập (uid) và khi đăng xuất (null).
 * Xoá luôn bộ nhớ tạm và báo cho toàn bộ giao diện vẽ lại.
 */
export function datTaiKhoan(uid) {
  if (uidHienTai === (uid || null)) return;
  uidHienTai = uid || null;
  memory = {};
  window.dispatchEvent(new Event("workspace-change"));
  window.dispatchEvent(new Event("tool-reset"));
}

export function taiKhoanHienTai() {
  return uidHienTai;
}
export function currentDraft(tool) {
  return memory[tool] || readWorkspace().drafts[tool]?.values || {};
}
export function stageDraft(tool, values) {
  memory[tool] = values;
  window.dispatchEvent(new Event("tool-reset"));
}
export function saveScenario(tool, name, summary) {
  const data = readWorkspace();
  if ((data.scenarios || []).length >= 20)
    throw new Error(
      "Đã có 20 kịch bản. Hãy xuất bản sao và bỏ bớt kịch bản trước khi lưu thêm.",
    );
  data.scenarios = [
    ...(data.scenarios || []),
    {
      id: crypto.randomUUID(),
      tool,
      name,
      values: structuredClone(currentDraft(tool)),
      summary,
      updated: new Date().toISOString(),
    },
  ];
  writeWorkspace(data);
}
export const normalize = (s) =>
  String(s)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toLowerCase();
function docKho(k) {
  try {
    const x = JSON.parse(localStorage.getItem(k));
    return validWorkspace(x)
      ? x
      : { version: 1, drafts: {}, saved: [], tasks: {}, profile: {} };
  } catch {
    return { version: 1, drafts: {}, saved: [], tasks: {}, profile: {} };
  }
}
export function readWorkspace() {
  return docKho(khoa());
}
export function writeWorkspace(data) {
  // capNhat là mốc để gộp hai thiết bị. Phải đóng dấu ở đây, chỗ duy nhất mọi
  // thay đổi đi qua — đóng dấu ở từng nơi gọi thì sớm muộn cũng có chỗ quên.
  localStorage.setItem(
    khoa(),
    JSON.stringify({ ...data, version: 1, capNhat: new Date().toISOString() }),
  );
  window.dispatchEvent(new Event("workspace-change"));
}
export function useToolState(tool, field, initial) {
  const [value, setValue] = useState(() => {
    if (!memory[tool])
      memory[tool] = readWorkspace().drafts[tool]?.values || {};
    if (Object.prototype.hasOwnProperty.call(memory[tool], field))
      return memory[tool][field];
    return typeof initial === "function" ? initial() : initial;
  });
  useEffect(() => {
    memory[tool] = { ...memory[tool], [field]: value };
  }, [tool, field, value]);
  const setVaGhiNhan = useCallback(
    (v) => {
      trackOnce("start:" + tool, "tool_start", { tool });
      setValue(v);
    },
    [tool],
  );
  return [value, setVaGhiNhan];
}
export function saveDraft(tool, summary) {
  const data = readWorkspace();
  data.drafts[tool] = {
    values: memory[tool] || {},
    summary,
    updated: new Date().toISOString(),
  };
  writeWorkspace(data);
}
/**
 * Ghi dấu xoá.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * VÌ SAO CẦN: gộp dữ liệu hai thiết bị theo kiểu "bên nào có thì giữ" khiến
 * thứ đã xoá sống lại. Xoá bản nháp runway trên điện thoại, đồng bộ với máy
 * tính còn bản cũ → nó quay về. Người dùng xoá lần nữa, nó lại về. Không có
 * cách nào thoát nếu chỉ nhìn vào dữ liệu hiện có.
 *
 * Nên phải ghi lại rằng "mục này đã bị xoá lúc nào". Lúc gộp, dấu xoá mới hơn
 * lần sửa cuối của mục thì mục đó biến mất thật.
 * ─────────────────────────────────────────────────────────────────────────
 */
export function datDauXoa(data, khoaMuc) {
  return { ...data, xoa: { ...(data.xoa || {}), [khoaMuc]: new Date().toISOString() } };
}

/** Cập nhật hồ sơ kèm mốc thời gian TỪNG Ô.
 *
 *  Không có mốc riêng từng ô thì không phân biệt được "ô này chưa ai điền" với
 *  "ô này vừa bị xoá trắng" — và bản gộp sẽ lấy lại giá trị cũ, tức là hoàn tác
 *  thao tác xoá của người dùng. */
export function capNhatProfile(data, patch) {
  const luc = new Date().toISOString();
  const moc = { ...(data.profileMoc || {}) };
  for (const k of Object.keys(patch)) moc[k] = luc;
  return { ...data, profile: { ...(data.profile || {}), ...patch }, profileMoc: moc };
}

export function resetDraft(tool) {
  delete memory[tool];
  const data = readWorkspace();
  delete data.drafts[tool];
  writeWorkspace(datDauXoa(data, `draft:${tool}`));
}

/** Xoá bộ nhớ tạm của form. Gọi sau khi nhận dữ liệu đồng bộ, nếu không người
 *  dùng mở công cụ ra vẫn thấy giá trị cũ dù hồ sơ đã cập nhật. */
export function lamMoiBoNhoTam() {
  memory = {};
  window.dispatchEvent(new Event("tool-reset"));
}
export function clearWorkspace() {
  // Chỉ xoá kho của người đang dùng, không đụng kho của tài khoản khác trên
  // cùng trình duyệt.
  localStorage.removeItem(khoa());
  memory = {};
  window.dispatchEvent(new Event("workspace-change"));
}
export function downloadText(name, text, type = "text/plain;charset=utf-8") {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
